import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { getTramsInvoiceNumberFromMetadata } from '@shared/invoice-display';
import {
  canonicalizeTravelTypeLabel,
  isSelectableTravelType,
} from '@/components/invoicing/travelType';
import InvoiceFormCard, {
  invoiceFormSchema,
  type InvoiceFormValues,
  formatMoneyFieldValue,
} from '@/components/invoicing/InvoiceFormCard';
import TripSection, {
  DEFAULT_TRIP_SECTION,
  type TripSectionValue,
} from '@/components/invoicing/TripSection';
import { useAtomicInvoiceSubmit } from '@/hooks/useInvoices';
import { useCurrentTramsContext } from '@/hooks/useTramsAccounts';
import type { UnclaimedInvoiceRow } from '@/hooks/useUnclaimed';
import type { Trip } from '@shared/schema';

/**
 * Spec — Unclaimed Commission.docx (Claim Action → Create new invoice):
 *   "System creates a new invoice pre-populated with fields from the
 *    unclaimed invoice; user completes remaining fields using the standard
 *    Create Invoice flow (Trip, Description, etc.)."
 *
 * This component is the embedded Create Invoice form for a single row in
 * the claim wizard. It owns its own RHF instance and trip selection. The
 * parent wizard drives row-to-row navigation via `key`/remount and
 * extracts drafts via the imperative ref before unmounting.
 */

const EMPTY_INVOICE: InvoiceFormValues = {
  travelerFirstName: '',
  travelerLastName: '',
  supplierLinkNo: undefined,
  supplierName: '',
  travelType: '',
  propertyName: '',
  shipName: '',
  cabinCount: undefined,
  tourName: '',
  description: '',
  confirmationNumber: '',
  departureDate: '',
  returnDate: '',
  totalFare: '',
  commissionAmount: '',
  agencyLinkNo: 0,
};

/** Strip an ISO datetime to `YYYY-MM-DD` so the date input accepts it. */
function toDateInputValue(raw: string | null | undefined): string {
  if (!raw) return '';
  // Accept already-trimmed `YYYY-MM-DD` and full ISO equally.
  return raw.length >= 10 ? raw.slice(0, 10) : '';
}

/**
 * Map an unclaimed-row snapshot onto the InvoiceFormValues shape.
 * Travel type is validated against the canonical `TRAVEL_TYPES` list — if
 * the snapshot's value isn't a known label we leave the field blank so the
 * user picks one (driven by the supplier's allowed types in InvoiceFormCard).
 */
export function snapshotToFormValues(
  row: UnclaimedInvoiceRow,
): InvoiceFormValues {
  // Canonicalize the snapshot's travel type (dash/spacing/alias-insensitive).
  // Only keep it when it resolves to a user-selectable type; otherwise leave
  // blank so the advisor picks one (driven by the supplier's allowed types).
  const rawType = row.travelType?.trim() ?? '';
  const travelType =
    rawType && isSelectableTravelType(rawType)
      ? canonicalizeTravelTypeLabel(rawType)
      : '';
  // Split primaryTraveler into first/last for form
  const rawTraveler = row.primaryTraveler ?? '';
  const travelerParts = rawTraveler.split('/');
  const travelerLastName = travelerParts[0] ?? '';
  const travelerFirstName = travelerParts.slice(1).join('/') ?? '';
  return {
    ...EMPTY_INVOICE,
    travelerFirstName,
    travelerLastName,
    supplierLinkNo: row.supplierLinkNo ?? undefined,
    supplierName: row.supplierName ?? '',
    travelType,
    confirmationNumber: row.confirmationNumber ?? '',
    departureDate: toDateInputValue(row.departureDate),
    returnDate: toDateInputValue(row.returnDate),
    totalFare: row.totalFare != null ? formatMoneyFieldValue(row.totalFare) : '',
    commissionAmount: row.commissionAmount != null ? formatMoneyFieldValue(row.commissionAmount) : '',
  };
}

export interface NewInvoiceStepDraft {
  formValues: InvoiceFormValues;
  tripSection: TripSectionValue;
}

export interface NewInvoiceSubmitResult {
  tripId: number;
  trip: Trip;
  invoiceId: number;
  invoiceNumber: string | null;
}

export interface NewInvoiceStepHandle {
  /** Pull the current form + trip state for draft persistence. */
  getDraft: () => NewInvoiceStepDraft;
  /** Whether the user has edited the prefilled form or trip selection. */
  isDirty: () => boolean;
  /**
   * Validate the form + trip section, run the atomic create-invoice POST,
   * and return the resulting trip + invoice IDs. Throws on validation or
   * network failure (caller toasts).
   */
  submit: () => Promise<NewInvoiceSubmitResult>;
}

interface NewInvoiceStepProps {
  row: UnclaimedInvoiceRow;
  /** When supplied, overrides snapshot-derived defaults (e.g. saved draft). */
  initialFormValues?: InvoiceFormValues;
  initialTripSection?: TripSectionValue;
  /** Idempotency key for the atomic create-invoice POST. */
  invoiceIdempotencyKey: string;
}

const NewInvoiceStep = forwardRef<NewInvoiceStepHandle, NewInvoiceStepProps>(
  function NewInvoiceStep(
    { row, initialFormValues, initialTripSection, invoiceIdempotencyKey },
    ref,
  ) {
    const { toast } = useToast();
    const atomicSubmit = useAtomicInvoiceSubmit();
    const { data: tramsData } = useCurrentTramsContext();
    const currentTramsAccountId = tramsData?.context?.tramsAccountId;

    const [tripSection, setTripSection] = useState<TripSectionValue>(
      initialTripSection ?? DEFAULT_TRIP_SECTION,
    );

    const form = useForm<InvoiceFormValues>({
      resolver: zodResolver(invoiceFormSchema),
      defaultValues: initialFormValues ?? snapshotToFormValues(row),
      mode: 'onBlur',
    });

    // Keep the form in sync if the parent swaps in a new draft for the
    // SAME mounted instance (e.g. parent recovers a draft on Back). When
    // the parent navigates rows we remount via `key`, so this guard is
    // primarily for the "in-place restore" edge.
    useEffect(() => {
      if (initialFormValues) form.reset(initialFormValues);
      // We only react to identity changes from the parent — not to user edits.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialFormValues]);

    const validateTripSection = (): string | null => {
      if (!tripSection.mode) {
        return 'Please select or create a trip before submitting.';
      }
      if (tripSection.mode === 'existing' && !tripSection.existingTripId) {
        return 'Please select a trip.';
      }
      if (tripSection.mode === 'new') {
        if (!tripSection.newTrip.name.trim()) {
          return 'Trip name is required.';
        }
        if (tripSection.newTrip.destinations.length === 0) {
          return 'At least one destination is required.';
        }
      }
      return null;
    };

    useImperativeHandle(
      ref,
      () => ({
        getDraft: () => ({
          formValues: form.getValues(),
          tripSection,
        }),
        isDirty: () => {
          const baselineForm = initialFormValues ?? snapshotToFormValues(row);
          const baselineTrip = initialTripSection ?? DEFAULT_TRIP_SECTION;
          return (
            JSON.stringify(form.getValues()) !== JSON.stringify(baselineForm) ||
            JSON.stringify(tripSection) !== JSON.stringify(baselineTrip)
          );
        },
        submit: async () => {
          const tripError = validateTripSection();
          if (tripError) {
            toast({
              title: 'Trip Required',
              description: tripError,
              variant: 'destructive',
            });
            throw new Error(tripError);
          }

          // The Total Fare + Commission inputs are hidden in this wizard —
          // their values come straight from the unclaimed snapshot. If the
          // snapshot is missing either amount, RHF would fail validation
          // with no visible field to fix, leaving the user stuck. Surface
          // a specific error in that (rare) case so the user knows to use
          // the existing-invoice path or contact support.
          const values = form.getValues();
          if (!values.totalFare || !values.commissionAmount) {
            const msg =
              'This unclaimed row is missing fare or commission amount data and cannot be auto-claimed. Please attach to an existing AIVIA invoice instead, or contact support.';
            toast({
              title: 'Missing snapshot data',
              description: msg,
              variant: 'destructive',
            });
            throw new Error(msg);
          }

          // RHF validation — surface inline messages.
          const valid = await form.trigger();
          if (!valid) {
            throw new Error('Please fix the highlighted fields.');
          }
          const parsed = invoiceFormSchema.safeParse(form.getValues());
          if (!parsed.success) {
            throw new Error(
              parsed.error.issues.map((e) => e.message).join(', '),
            );
          }

          const tripPayload =
            tripSection.mode === 'existing'
              ? { mode: 'existing' as const, tripId: tripSection.existingTripId! }
              : {
                  mode: 'new' as const,
                  name: tripSection.newTrip.name,
                  destinations: tripSection.newTrip.destinations,
                  isGroup: tripSection.newTrip.isGroup,
                  groupName: tripSection.newTrip.groupName || undefined,
                };

          const result = await atomicSubmit.mutateAsync({
            trip: tripPayload,
            invoices: [
              {
                ...parsed.data,
                primaryTraveler: `${parsed.data.travelerLastName}/${parsed.data.travelerFirstName}`,
                departureDate: new Date(`${parsed.data.departureDate}T00:00:00Z`).toISOString(),
                returnDate: new Date(`${parsed.data.returnDate}T00:00:00Z`).toISOString(),
                ...(currentTramsAccountId
                  ? { tramsAccountId: currentTramsAccountId }
                  : {}),
              },
            ],
            idempotencyKey: invoiceIdempotencyKey,
            // Unclaimed-claim pre-creation: suppresses the invoice-creation
            // confirmation email (the claim has its own review flow + email).
            context: 'unclaimed',
          });

          const trip = result.trip;
          const created = result.invoices?.[0];
          if (!trip?.id || !created) {
            throw new Error('Invoice creation returned an empty response.');
          }
          return {
            tripId: trip.id,
            trip,
            invoiceId: created.id,
            invoiceNumber: getTramsInvoiceNumberFromMetadata(created.metadata),
          };
        },
      }),
      // Stable identity wrt these — the imperative methods read live state.
      [
        form,
        tripSection,
        row,
        initialFormValues,
        initialTripSection,
        atomicSubmit,
        currentTramsAccountId,
        invoiceIdempotencyKey,
        toast,
      ],
    );

    return (
      <div className="space-y-4">
        {atomicSubmit.isPending && (
          <div className="flex items-center gap-2 rounded-md bg-blue-50 p-2 text-xs text-blue-900 dark:bg-blue-950 dark:text-blue-200">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Creating invoice…
          </div>
        )}
        <TripSection value={tripSection} onChange={setTripSection} />
        <FormProvider {...form}>
          <form onSubmit={(e) => e.preventDefault()}>
            <InvoiceFormCard
              form={form}
              index={0}
              canRemove={false}
              hideFinancialFields
              compactMode
            />
          </form>
        </FormProvider>
      </div>
    );
  },
);

export default NewInvoiceStep;
